import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useMarkersConnection from "./useMarkesConnection";
import * as Styled from "./styles";
import { SwipeableDrawer } from "@mui/material";
import { renderToString } from "react-dom/server";

import { importLibrary, setOptions } from "@googlemaps/js-api-loader";

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import useMarkesModalOrderAll from "./useMarkesModalOrderAll";
import { handleGetLogisticUsersRequest } from "store/modules/DealerUsers/thunk.store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useAuth } from "context/AuthContext";
import { LogisticUsersProps } from "store/modules/DealerUsers/types.store";
import { OrderProps } from "store/modules/Orders/types.store";
import { useSignalR } from "context/SignalRContext";
import { format } from "date-fns";
import { PIN } from "./utils";

type AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;

const Tracking: React.FC = () => {
  const {
    map,
    setMap,
    markers,
    setMarkers,
    setLatInput,
    setLngInput,
    load,
    setLoad,
    mapDivRef,
    mapInitialState,
    setMapInitialState,
    markersClick,
    setMarkersClick,
    markerLibrary,
    setMarkerLibrary,
  } = useMarkersConnection();

  const {
    modalRender,
    list,
    toggleDrawer,
    state,
    setState,
    setCurrentTab,
    setPinSelected,
    setDriveSelected,
    markerAndDrawLine,
    setMarkerAndDrawLine,
  } = useMarkesModalOrderAll();
  const [allIUsers, setAllIUsers] = useState<LogisticUsersProps[]>();
  const { dealerId } = useAuth();
  const { allOrders } = useAppSelector((stateOrder) => stateOrder.orders);
  const dispatch = useAppDispatch();
  const { ordersSignal, lastOrderSignal } = useSignalR();

  const [ordersSelected, setOrdersSelected] = useState<any>();
  const [isFirstRenderOrder, setIsFirstRenderOrder] = useState(true);

  const handledUsers = useCallback(async () => {
    const response = await dispatch(
      handleGetLogisticUsersRequest({
        dealerId: dealerId || "",
      }),
    ).unwrap();

    const driverListToday = response.data.filter((driver) => {
      const lastUpdateDate = new Date(driver.lastDriverUpdate)
        .toISOString()
        .split("T")[0];
      const todayDate = new Date().toISOString().split("T")[0];
      return lastUpdateDate === todayDate;
    });

    setAllIUsers(driverListToday ?? []);
  }, [dispatch, dealerId]);

  useEffect(() => {
    handledUsers();
  }, [handledUsers]);

  const dealerInfo = localStorage.getItem("@ctech:dealerInfo");

  const dealerInfoJson = useMemo(
    () => JSON.parse(dealerInfo ?? ""),
    [dealerInfo],
  );

  const createMarkerContent = (iconUrl: string, title?: string) => {
    const markerDiv = document.createElement("div");
    markerDiv.innerHTML = `
      <img src="${iconUrl}" alt="${title || "marker"}" />
    `;
    return markerDiv;
  };

  const initMap = useCallback(
    async (markerLib: any) => {
      const mapOptions: google.maps.MapOptions = {
        center: {
          lat: Number(dealerInfoJson.address.latitude),
          lng: Number(dealerInfoJson.address.longitude),
        },
        zoom: 14,
        streetViewControl: false,
        mapId: "TRACKING_MAP_ID",
      };

      const newMap = new google.maps.Map(
        document.getElementById("map")!,
        mapOptions,
      );

      setMap(newMap);

      const { AdvancedMarkerElement } = markerLib;

      const initialMarkers: AdvancedMarkerElement[] = [
        {
          position: {
            lat: Number(dealerInfoJson.address.latitude),
            lng: Number(dealerInfoJson.address.longitude),
          },
          title: dealerInfoJson.legalName,
          icon: PIN.DEALER,
        },
      ].map(({ position, title, icon }) => {
        const markerContent = createMarkerContent(icon, title);

        const marker = new AdvancedMarkerElement({
          position,
          map: newMap,
          title,
          content: markerContent,
        });

        return marker;
      });

      setMarkers(initialMarkers);
      setMapInitialState(true);
    },
    [dealerInfoJson, setMap, setMarkers, setMapInitialState],
  );

  const initializeGoogleMapScript = useCallback(() => {
    (async () => {
      if (!load) {
        setLoad(true);

        try {
          setOptions({ key: process.env.REACT_APP_GOOGLE_MAPS_KEY });
          await importLibrary("maps");
          const markerLib = await importLibrary("marker");
          setMarkerLibrary(markerLib);

          initMap(markerLib);
        } catch {
          setLoad(false);
        }
      }
    })();
  }, [load, initMap, setMarkerLibrary, setLoad]);

  useEffect(() => {
    initializeGoogleMapScript();
  }, [initializeGoogleMapScript]);

  const [flightPath, setFlightPath] = useState<null | any>(null);

  useEffect(() => {
    if (markerAndDrawLine && markers?.length >= 2) {
      const order = ordersSignal?.find(
        (data) => data?.id === markerAndDrawLine?.id,
      );
      const driverSelected = markers?.find(
        (data: any) => data?.id === order?.driver?.id,
      );

      if (markerAndDrawLine?.position && driverSelected?.position) {
        const newFlightPath = new window.google.maps.Polyline({
          path: [markerAndDrawLine?.position, driverSelected?.position],
          geodesic: true,
          strokeColor: "#FF0000",
          strokeOpacity: 1.0,
          strokeWeight: 2,
        });

        newFlightPath?.setMap(map);

        setFlightPath(newFlightPath as any);
      }
    } else {
      if (flightPath) flightPath?.setMap(null as any);
      setFlightPath(null);
    }
  }, [markerAndDrawLine, allOrders, ordersSignal, markers, map, flightPath]);

  const handleMarkerOrder = useCallback(() => {
    if (ordersSelected && ordersSelected?.position) {
      const order = ordersSignal?.find(
        (data) => data?.id === ordersSelected.id,
      );

      const iconUrl = ordersSelected?.iconUrl;

      if (iconUrl === PIN.ORDER) {
        setState(true);

        if (order?.dealerState === "InProgress") {
          setCurrentTab("2");
        }
        if (order?.dealerState === "New") {
          setCurrentTab("1");
        }
        setPinSelected({ type: "order", id: ordersSelected.id });
        setOrdersSelected(null);
      }
      if (iconUrl === PIN.DRIVER) {
        setDriveSelected({
          label: ordersSelected.title ?? "",
          id: ordersSelected.id,
        });
        setState(true);
        setCurrentTab("1");
        setPinSelected({ type: "Driver", id: ordersSelected.id });
        setOrdersSelected(null);
      }
    }
  }, [
    ordersSelected,
    ordersSignal,
    setCurrentTab,
    setPinSelected,
    setOrdersSelected,
    setDriveSelected,
    setState,
  ]);

  useEffect(() => {
    handleMarkerOrder();
  }, [handleMarkerOrder]);

  useEffect(() => {
    if (markersClick) {
      setOrdersSelected(markersClick);
      setMarkersClick();
    }
  }, [markersClick, setOrdersSelected, setMarkersClick]);

  const handleAddMarker = useCallback(
    async (
      latInput: number,
      lngInput: number,
      id: string,
      typePin: string,
      pinIcon?: any,
    ) => {
      if (map && latInput && lngInput && markerLibrary) {
        const newLat = latInput;
        const newLng = lngInput;

        if (!!newLat && !!newLng) {
          const { AdvancedMarkerElement } = markerLibrary;

          const iconUrl = pinIcon ?? PIN.DRIVER;
          const markerContent = createMarkerContent(iconUrl, typePin);

          const newMarker = new AdvancedMarkerElement({
            position: { lat: newLat, lng: newLng },
            map,
            title: typePin,
            content: markerContent,
          });

          newMarker.id = id;
          newMarker.iconUrl = iconUrl;

          newMarker.addListener("click", () => {
            setOrdersSelected(newMarker);

            if (pinIcon === PIN.ORDER) {
              setMarkerAndDrawLine(newMarker);
            }
          });

          if (iconUrl === PIN.DRIVER) {
            const foundDriver = allIUsers?.find((driver) => driver.id === id);

            if (foundDriver) {
              const infoContent = renderToString(
                <Styled.InfoWindowContainer>
                  <Styled.TopInfoWindowContainer>
                    <Styled.DriverName>
                      {foundDriver?.driverName}
                    </Styled.DriverName>

                    <Styled.OnlineBadge>
                      {foundDriver?.driverIsActive
                        ? "Disponível"
                        : "Indisponível"}
                    </Styled.OnlineBadge>
                  </Styled.TopInfoWindowContainer>

                  <Styled.LastUpdateText>
                    Última atualização:{" "}
                    {format(
                      new Date(foundDriver?.lastDriverUpdate || ""),
                      "dd/MM 'às' HH:mm",
                    )}
                  </Styled.LastUpdateText>
                </Styled.InfoWindowContainer>,
              );

              const infowindow = new window.google.maps.InfoWindow({
                content: infoContent,
              });

              const markerElement = newMarker.element;
              if (markerElement) {
                markerElement.addEventListener("mouseenter", () =>
                  infowindow.open(map, newMarker),
                );
                markerElement.addEventListener("mouseleave", () => {
                  infowindow.close();
                });
              }
            }
          }

          setMarkers((prev: AdvancedMarkerElement[]) => [...prev, newMarker]);

          setLatInput("");
          setLngInput("");
        }
      }
    },
    [
      allIUsers,
      map,
      markerLibrary,
      setLatInput,
      setLngInput,
      setMarkerAndDrawLine,
      setMarkers,
    ],
  );

  const removeMarkerById = useCallback(
    (id: string) => {
      setMarkers((prevMarkers: AdvancedMarkerElement[]) =>
        prevMarkers.filter((marker) => {
          const markerId = marker.id;

          if (markerId !== undefined && markerId === id) {
            marker.map = null;
            return false;
          }

          return markerId !== undefined && markerId !== id;
        }),
      );
    },
    [setMarkers],
  );

  useEffect(() => {
    if (
      mapInitialState &&
      lastOrderSignal &&
      lastOrderSignal.dealerState !== "Finalized" &&
      !markers?.find(
        (marker: AdvancedMarkerElement) => marker?.id === lastOrderSignal?.id,
      )
    ) {
      handleAddMarker(
        lastOrderSignal.delivery.address.latitude,
        lastOrderSignal.delivery.address.longitude,
        lastOrderSignal.id,
        lastOrderSignal.consumer.name,
        PIN.ORDER,
      );
    }

    if (lastOrderSignal && lastOrderSignal.dealerState === "Finalized") {
      removeMarkerById(lastOrderSignal.id);
    }
  }, [
    lastOrderSignal,
    ordersSignal,
    mapInitialState,
    markers,
    handleAddMarker,
    removeMarkerById,
  ]);

  const isFirstRender = useRef(true);
  const handleFirstAddMarkerDriver = useCallback(() => {
    if (
      mapInitialState &&
      !!allIUsers &&
      allIUsers?.length > 0 &&
      isFirstRender.current
    ) {
      isFirstRender.current = false;
      allIUsers.forEach((driver) => {
        handleAddMarker(
          driver.latitude,
          driver.longitude,
          driver.driverId,
          driver.driverName,
          PIN.DRIVER,
        );
      });
    }
  }, [mapInitialState, allIUsers, handleAddMarker]);

  useEffect(() => {
    handleFirstAddMarkerDriver();
  }, [handleFirstAddMarkerDriver]);

  const handleAddMarkerOrderSignalR = useCallback(() => {
    if (mapInitialState) {
      const ordersSignalFilter =
        allOrders?.filter((x: any) => x.dealerState !== "Finalized") || [];
      if (ordersSignalFilter.length && isFirstRenderOrder) {
        setIsFirstRenderOrder(false);

        ordersSignalFilter.map((data: OrderProps) => {
          return handleAddMarker(
            data.delivery.address.latitude,
            data.delivery.address.longitude,
            data.id,
            data.consumer.name,
            PIN.ORDER,
          );
        });
      }
    }
  }, [allOrders, handleAddMarker, mapInitialState, isFirstRenderOrder]);

  useEffect(() => {
    handleAddMarkerOrderSignalR();
  }, [handleAddMarkerOrderSignalR]);

  return (
    <Styled.Container>
      {modalRender}
      <React.Fragment key="right">
        <Styled.ContainerButton
          style={{
            position: "absolute",
            top: "35vh",
            right: state ? 500 : 0,
            zIndex: 10000,
            transition: "all 165ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
          }}
          onClick={toggleDrawer("right", !state)}
        >
          {!state ? (
            <ChevronLeftIcon color="error" />
          ) : (
            <ChevronRightIcon color="error" />
          )}
        </Styled.ContainerButton>
        <SwipeableDrawer
          anchor="right"
          open={state}
          onClose={toggleDrawer("right", false)}
          onOpen={toggleDrawer("right", true)}
        >
          {list("right")}
        </SwipeableDrawer>
      </React.Fragment>

      <Styled.Map ref={mapDivRef} id="map" />
    </Styled.Container>
  );
};

export default Tracking;
